import React, { useState } from "react";
import axios from "axios";
import { UserPlus, Code, Tag, CheckSquare, Settings, Loader2 } from 'lucide-react'; // Added Loader2 icon

const CreateAccount = () => {
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    type: "Asset",
    isTax: false,
  });

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [loading, setLoading] = useState(false);

  const accountTypes = [
    "Asset",
    "Liability",
    "Equity",
    "Income",
    "Expense",
    "Supplier",
    "Customer",
    "Gst Tax",
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setMessageType("");
    
    if (!formData.name) {
        setMessage("Account Name is required.");
        setMessageType("error");
        setLoading(false);
        return;
    }

    try {
      // NOTE: You must replace 'axios' and 'http://localhost:4000' with a Firebase/Firestore implementation 
      // if this were a production application running on Canvas. 
      // For this isolated component, we keep the original data fetching method.
      const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:4000/api';
      await axios.post(
        `${apiBase}/accounts/create?db=alwaysdata`,
        {
          ...formData,
          isTax: formData.isTax ? 1 : 0,
        }
      );
      setMessage("Account created successfully!");
      setMessageType("success");
      setFormData({ code: "", name: "", type: "Asset", isTax: false });
    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.message || "Failed to create account");
      setMessageType("error");
    } finally {
        setLoading(false);
    }
  };

  // Common input styling for a clean look with focused state emphasis
  const inputClass = "w-full p-3 border border-gray-300 bg-gray-50 text-gray-800 rounded-xl transition duration-300 shadow-inner " + 
                     "focus:outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-100 focus:shadow-md";
                     
  // Common label styling
  const labelClass = "block text-gray-700 font-semibold mb-2 flex items-center text-sm";


  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4 sm:p-6 font-sans">
      <div className="w-full max-w-lg mx-auto bg-white p-6 sm:p-8 rounded-3xl shadow-2xl border-4 border-sky-400/50">
        
        {/* Header */}
        <div className="text-center mb-8 border-b-4 border-sky-200 pb-4">
          <h2 className="text-4xl font-extrabold text-sky-800 flex items-center justify-center tracking-tight">
            <UserPlus className="h-8 w-8 mr-3 text-sky-600" />
            New <span className="text-sky-500 ml-2">Account</span>
          </h2>
          <p className="text-gray-500 mt-2 text-md">
            Define a new entry in your Chart of Accounts.
          </p>
        </div>

        {/* Message Alert */}
        {message && (
          <div
            className={`p-4 mb-6 rounded-xl text-md font-medium border ${
              messageType === "success"
                ? "bg-green-100 text-green-700 border-green-400"
                : "bg-red-100 text-red-700 border-red-400"
            }`}
          >
            {message}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Code Input */}
          <div>
            <label className={labelClass}>
                <Code className="h-4 w-4 mr-2 text-sky-600" />
                Account Code (Optional)
            </label>
            <input
              type="text"
              name="code"
              value={formData.code}
              onChange={handleChange}
              className={inputClass}
              placeholder="e.g., 1000 or S-2025-06"
            />
          </div>

          {/* Name Input */}
          <div>
            <label className={labelClass}>
                <Tag className="h-4 w-4 mr-2 text-sky-600" />
                Account Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={inputClass}
              placeholder="e.g., Cash at Bank, Sales Revenue"
              required
            />
          </div>

          {/* Type Select */}
          <div>
            <label className={labelClass}>
                <Settings className="h-4 w-4 mr-2 text-sky-600" />
                Account Type
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className={`${inputClass} appearance-none cursor-pointer`}
            >
              {accountTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          {/* Tax Checkbox */}
          <div className="flex items-center pt-2 pb-4 border-b border-gray-200">
            <input
              type="checkbox"
              id="isTax"
              name="isTax"
              checked={formData.isTax}
              onChange={handleChange}
              // Enhanced checkbox style
              className="h-5 w-5 text-sky-600 border-gray-300 rounded focus:ring-sky-500 shadow-sm transition duration-200 cursor-pointer"
            />
            <label htmlFor="isTax" className="ml-3 text-gray-700 font-medium flex items-center">
                <CheckSquare className="h-4 w-4 mr-2 text-sky-600" />
                Is Tax Account
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full font-bold p-4 rounded-xl text-lg transition-all duration-300 shadow-xl mt-8 flex items-center justify-center transform ${
                loading 
                ? 'bg-gray-400 text-gray-700 cursor-not-allowed' 
                : 'bg-sky-600 text-white hover:bg-sky-700 hover:scale-[1.01] hover:shadow-2xl'
            }`}
          >
            {loading ? (
                <>
                    <Loader2 className="animate-spin h-5 w-5 mr-3" />
                    Creating...
                </>
            ) : (
                "Create Account"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateAccount;
